import { useEffect, useMemo, useRef, useState } from 'react';

// Compact popover multi-select: a button showing "All" / "N selected", opening a
// checkbox list with search + Select-all / Clear. Closes on outside click / Esc.
export default function MultiSelect({ options, selected, onChange, allLabel = 'All', label, searchable = true }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey); };
  }, [open]);

  const selectedSet = useMemo(() => new Set(selected), [selected]);
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? options.filter((o) => o.label.toLowerCase().includes(q)) : options;
  }, [options, query]);

  const toggle = (val) => {
    const next = new Set(selectedSet);
    if (next.has(val)) next.delete(val);
    else next.add(val);
    onChange([...next]);
  };

  const summary = selected.length === 0 ? allLabel : `${selected.length} selected`;

  return (
    <div className="rep-ms" ref={ref}>
      {label && <span className="rep-ms-label">{label}</span>}
      <button type="button" className="rep-ms-btn" onClick={() => setOpen((o) => !o)} aria-haspopup="listbox" aria-expanded={open}>
        <span className={`rep-ms-summary${selected.length ? ' has' : ''}`}>{summary}</span>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>
      {open && (
        <div className="rep-ms-panel" role="listbox">
          <div className="rep-ms-actions">
            <button type="button" onClick={() => onChange(options.map((o) => o.value))}>Select all</button>
            <button type="button" onClick={() => onChange([])}>Clear</button>
          </div>
          {searchable && options.length > 8 && (
            <input
              className="rep-ms-search"
              placeholder="Search…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              autoFocus
            />
          )}
          <div className="rep-ms-list">
            {filtered.map((o) => (
              <label key={o.value} className="rep-ms-opt">
                <input type="checkbox" checked={selectedSet.has(o.value)} onChange={() => toggle(o.value)} />
                <span>{o.label}</span>
              </label>
            ))}
            {filtered.length === 0 && <p className="rep-ms-empty">No matches</p>}
          </div>
        </div>
      )}
    </div>
  );
}
